import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const timeSince = (dateValue: Date): string => {
  const date = new Date(dateValue);
  const now = new Date();
  const secondsPast = (now.getTime() - date.getTime()) / 1000;

  if (secondsPast < 0) {
    return "in the future";
  } else if (secondsPast < 60) {
    return `${Math.round(secondsPast)} seconds ago`;
  } else if (secondsPast < 3600) {
    return `${Math.round(secondsPast / 60)} minutes ago`;
  } else if (secondsPast < 86400) {
    return `${Math.round(secondsPast / 3600)} hours ago`;
  } else if (secondsPast >= 86400) {
    const days = Math.round(secondsPast / 86400);
    return `${days} days ago`;
  }

  // As a fallback, in case of any unexpected result.
  return "some time ago";
};

/**
 * `func`, called once calls stop for `waitFor` ms. `cancel` drops a call still
 * waiting, which a component unmounting must do: the call would otherwise land
 * after it is gone.
 */
export const debounce = <T extends (...args: any[]) => unknown>(func: T, waitFor: number) => {
  let timeout: number | undefined;

  const debounced = (...args: Parameters<T>): void => {
    const later = () => {
      clearTimeout(timeout);
      timeout = undefined;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = window.setTimeout(later, waitFor);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
    timeout = undefined;
  };
  return debounced;
};

export const simpleHash = (obj: Record<string, unknown>) => {
  const str = JSON.stringify(obj);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const getApiUrl = () => {
  return import.meta.env.VITE_API_URL || "/api";
};

/**
 * The API's origin, with no path — what the HTTP client uses as its `baseURL`
 * so that callers write full origin-relative paths (`/api/a/...`) and nothing
 * outside the `/api` mount (e.g. `/.well-known/...`) is reachable.
 *
 * Resolved through `URL` rather than by stripping `/api` off `getApiUrl()`:
 * that returns a bare `/api` in dev and in the admin build, and stripping it
 * would yield `""`, which axios treats as "no base" — a second code path that
 * also defeats any origin comparison against it.
 */
export function getApiOrigin(): string {
  return new URL(getApiUrl(), window.location.origin).origin;
}

/**
 * Normalize any API path form the server may hand us to the origin-relative
 * `/api/…` spelling, leaving everything else exactly as it was.
 *
 * Only the legacy mount-relative forms are rewritten. An app route (`/threads/42`)
 * passes through untouched — prefixing it would invent a URL nothing serves — and
 * so does anything supplying its own host, which cannot match a mount prefix
 * because those need a single leading slash (`//cdn.example/a/x` does not start
 * with `/a/`).
 *
 * The legacy arm exists for payloads frozen before the server switched to
 * origin-relative paths: thread-debug blobs, and shared-thread snapshots, which
 * persist hydrated artifacts verbatim (`api/src/context/snapshots.ts`). Both are
 * replayed through the live `<MessageList>`, so the paths they carry are
 * whatever was current at capture time.
 */
export function toApiMountPath(path: string): string {
  return path.startsWith("/a/") || path.startsWith("/p/") ? `/api${path}` : path;
}

/**
 * Resolve an API asset path — a schema-driven artifact view's image `src`, a
 * link button's `href` — to a URL the browser can load. The browser resolves a
 * bare path against the *page* origin, which in production is a different host
 * from the API, so these must be made absolute at render time.
 *
 * Only paths under the `/api` mount are absolutized. Anything else is returned
 * as given, whether it carries its own host (an external Google Drive link) or
 * is an app route the router owns (`/threads/42`).
 */
export const resolveApiPath = (path: string): string => {
  const mountPath = toApiMountPath(path);
  return mountPath.startsWith("/api/") ? `${getApiOrigin()}${mountPath}` : mountPath;
};

export const getSocketUrl = () => {
  // In production, we have a dedicated subdomain for the socket.io server
  // For local development or other environments, use the same URL the web app runs on
  // (dev uses the Vite proxy to handle routing to the api and socket.io servers)
  const urlString = import.meta.env.VITE_SOCKET_URL || "/api/socket.io";
  const url = new URL(urlString, window.location.origin); // fallback to window.location.origin if apiUrl is just a path
  return url;
};

export const getProjectId = () => {
  return import.meta.env.VITE_PROJECT_ID || "dev";
};

export const getAdminUrl = () => {
  if (import.meta.env.DEV) {
    return "http://localhost:5174";
  }
  return "https://admin.okthx.com";
};

export const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

// True when the *primary* pointing device is touch (phone/tablet). Unlike
// `isTouchDevice()`, this stays false on touch-capable desktops — most Windows
// laptops report `navigator.maxTouchPoints > 0` despite having a keyboard and
// mouse, so `isTouchDevice()` wrongly classifies them as mobile. Use this for
// "does the user have a keyboard" decisions (Enter-to-send, auto-focus).
export const isCoarsePointer = () => {
  return typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
};

const iosPlatforms = new Set([
  "iPad Simulator",
  "iPhone Simulator",
  "iPod Simulator",
  "iPad",
  "iPhone",
  "iPod",
]);
export const isIOS = () => {
  // https://stackoverflow.com/a/9039885
  return (
    iosPlatforms.has(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && isTouchDevice())
  );
};
